<script>
import { mapActions, mapState } from "vuex";
import { formatPool } from "@/utils";
import FooterWrapper from "@/components/FooterWrapper";

export default {
  // Included components
  components: {
    FooterWrapper,
  },

  data() {
    return {
      // The new user's email
      poolAccount: "",

      poolId: "",
    };
  },

  computed: {
    // Options for the pool selector
    poolOptions() {
      return this.pools.map((pool) => {
        return {
          value: pool.id,
          text: formatPool(pool, true),
        };
      });
    },

    // Mapped vuex state
    ...mapState(["pools", "teamContracts", "activeTeamId"]),
  },

  // When the page is created, get the options for pools
  async created() {
    await this.getPools();
    await this.getTeamContracts();
  },

  methods: {
    // On submit, set pool autoconfig (pool, pool account, default device), and route to onboarding complete page
    async submit() {
      let result = await this.setTeamPoolSettings({
        poolId: this.poolId,
        poolAccount: this.poolAccount,
        device_type: this.teamContracts[0].device_type.id,
      });

      let success = await this.putContractAsOnboardingUser({
        teamId: this.activeTeamId,
        contractId: this.teamContracts[0].id,
        // Here we set the onboarding_invoice_created to true
        // This function fires when it receives an event from the backend that the stripe invoice was created
        onboarding_invoice_created:
          this.teamContracts[0].onboarding_invoice_created,
        payment_method_ingested: this.teamContracts[0].payment_method_ingested,
        // Here we set the pool_info_capture to true
        // This function fires when it receives an event from the backend that the pool info was captured
        pool_info_captured: true,
      });

      if (result && success) {
        this.$router.push({ path: "/onboarding-complete" });
      }
    },

    skip() {
      this.$router.push({ path: "/onboarding-complete" });
    },

    // Mapped vuex actions
    ...mapActions([
      "getPools",
      "setTeamPoolSettings",
      "getTeamContracts",
      "putContractAsOnboardingUser",
    ]),
  },
};
</script>

<template>
  <FooterWrapper>
    <b-container class="mt-3">
      <b-row align-h="center">
        <b-col lg="9" xl="8">
          <b-form @submit.stop.prevent="submit">
            <b-row class="mb-3">
              <b-col>
                <br />
                <h1 class="center">Select a Mining Pool</h1>
              </b-col>
            </b-row>

            <div>
              <hr size="3" />
              <h6>Please Select a Mining Pool and Provide your Pool Account</h6>
              <br />
            </div>

            <b-card>
              <b-card-text>
                <b-form-group
                  id="destination-worker-pool-group"
                  label="Pool"
                  label-for="destinationWorkerPool"
                >
                  <b-form-select
                    required
                    v-model="poolId"
                    id="destinationWorkerPool"
                    :options="poolOptions"
                  >
                    <template #first>
                      <option :value="null" disabled>Pool</option>
                    </template>
                  </b-form-select>
                </b-form-group>

                <b-form-group
                  id="password-group"
                  label="Pool Account"
                  label-for="password"
                >
                  <b-form-input
                    required
                    id="password"
                    v-model="poolAccount"
                    placeholder="Pool Account"
                    minlength="1"
                  ></b-form-input>
                </b-form-group>
              </b-card-text>
            </b-card>
            <br />
            <div class="right">
              <b-button type="submit" variant="primary">
                Complete Pool Selection
              </b-button>
            </div>
            <div class="spacer" />
            <div class="right">
              <b-button @click="skip">Skip</b-button>
            </div>
          </b-form>
          <div>
            <br />
            <hr size="3" />
            <br />
            <h4 class="center">Frequently Asked Questions</h4>
            <br />
          </div>
          <div>
            <div class="divider">
              <b-button v-b-toggle="'collapse-1'" class="collapsible">
                What is a Mining Pool?
              </b-button>
              <b-collapse id="collapse-1">
                <div class="responseText">
                  <p>
                    A mining pool is a group of cryptocurrency miners who
                    combine their computational resources over a network to
                    increase the probability of mining a block and earning
                    mining rewards. Miners have two choices: mine with a pool or
                    by themselves. If a miner mines by themselves, they will get
                    the entirety of the block reward, however their probability
                    of mining a block is almost insurmountably small. Pools are
                    a way for miners to lower the variance in payouts and
                    stabilize the income from mining.
                  </p>
                </div>
              </b-collapse>
            </div>
            <div class="divider">
              <b-button v-b-toggle="'collapse-2'" class="collapsible">
                How do I select a Mining Pool?
              </b-button>
              <b-collapse id="collapse-2">
                <div class="responseText">
                  <p>
                    When choosing a mining pool, you should pay attention to the
                    following characteristics and evaluate them given your
                    hardware and other preferences:
                  </p>

                  <b-button v-b-toggle="'collapse-7'" class="mini-collapsible">
                    a. Compatibility
                  </b-button>
                  <b-collapse id="collapse-7">
                    <div class="responseText2">
                      <p>
                        It is critical to make sure that a pool you are
                        considering supports your hardware.
                      </p>
                      <p>
                        Additionally, ensure that the pool you are mining with
                        supports the algorithm and various chains that you wish
                        to mine on.
                      </p>
                    </div>
                  </b-collapse>

                  <b-button v-b-toggle="'collapse-8'" class="mini-collapsible">
                    b. Size
                  </b-button>
                  <b-collapse id="collapse-8">
                    <div class="responseText2">
                      <p>
                        The size of a mining pool, measured in hashrate, is an
                        important determinant of payout size and frequency.
                        Pools with a larger share of the network hashrate are
                        more likely to mine a block and thus earn the rewards.
                        Over a longer period of time, those pools will earn a
                        larger share of the block rewards. However, it is
                        important to note that personal rewards are still
                        proportional to the hashrate submitted. The benefit of
                        joining a larger is simply that the variance in payouts
                        is lower.
                      </p>
                      <p>
                        Smaller pools are subject to potentially longer periods
                        of time without mining a block because of their
                        comparatively smaller share of the network hashrate.
                      </p>
                    </div>
                  </b-collapse>

                  <b-button v-b-toggle="'collapse-9'" class="mini-collapsible">
                    c. Payment Scheme and Fee
                  </b-button>
                  <b-collapse id="collapse-9">
                    <div class="responseText2">
                      <p>
                        Pools use various methods of measuring hashrate
                        performance and crediting miners with work done. Below
                        we will cover common ones:
                      </p>
                      <p>
                        1.
                        <u>Pay-Per-Share (PPS)</u>
                        : Miners get paid for every valid share they contribute.
                        Each share’s value in BTC is calculated based on the
                        probability of a number of shares needed for a pool to
                        find a block. For example, if a pool needs 1000 shares
                        to find one block (or 6.25 BTC), each valid share is
                        worth 0.00625. You will always get paid no matter if the
                        pool finds a block or not, meaning the pool will pay out
                        from existing BTC reserves. Pools do not pay out the
                        transaction fees collected for blocks found.
                      </p>
                      <p>
                        2.
                        <u>Full Pay-Per-Share (FPPS)</u>
                        : This payment scheme is identical to PPS in paying out
                        block rewards, however, under this scheme, pools payout
                        the transaction fees earned on every mined block as
                        well. FPPS calculates a standard transaction fee within
                        a certain period of time and distributes to miners along
                        with block rewards.
                      </p>
                      <p>
                        3.
                        <u>Pay-Per-Last-N-Share (PPLNS)</u>
                        : Under this scheme, miners are only rewarded once the
                        pool has actually found a block. To find what your share
                        of this reward is, the pool goes back and checks the
                        valid shares that you submitted before the winning
                        block. Shares that are submitted closer in time to when
                        the block was found are more highly weighted to
                        incentivize sticking with the pool and not switching
                        back and forth for profit maximizing.
                      </p>
                      <p>
                        4.
                        <u>Pay-Per-Share+ (PPS+)</u>
                        : This is a combination of PPS and PPLNS. Block rewards
                        are paid out according to PPS, however, transaction fees
                        are paid out according to PPLNS, so miners who have
                        stuck with the pool for longer will be more likely to
                        receive larger shares of transaction fees.
                      </p>
                      <p>
                        5.
                        <u>Score (Scoring Hashrate)</u>
                        : This scheme uses an exponential moving average from
                        the hashrate of each user’s mining devices connected to
                        the pool. This way, any short drops or spikes in
                        hashrate will be smoothed out and reward calculations
                        will be more stable and fair. Your scoring hashrate at
                        the time the block was found as a relative share of the
                        pool’s total hashrate will be the relative share of the
                        reward you receive (less pool fees)
                      </p>
                      <p>
                        Pools often vary in how they charge fees. Some pools
                        offer special low-fee promotions to attract miners to
                        the pool. Some pools take extra fees if you set low
                        payout thresholds. Most fees hover around 2% of rewards
                        but vary by the pool. You should be sure to understand
                        the fee structure of your pool before you begin mining.
                      </p>
                    </div>
                  </b-collapse>

                  <b-button v-b-toggle="'collapse-10'" class="mini-collapsible">
                    d. Decentralization
                  </b-button>
                  <b-collapse id="collapse-10">
                    <div class="responseText2">
                      <p>
                        The beauty of cryptocurrencies lies in their
                        decentralized mechanisms on reaching consensus. Mining
                        pools, while better for the individual miner, are
                        inarguably a more centralized way of validating
                        transactions on a blockchain. If you are a miner who
                        cares about decentralization, you will likely want to
                        balance your choice of pool based on size and location
                        of the pool as to not increase the degree of
                        centralization on the network.
                      </p>
                    </div>
                  </b-collapse>

                  <b-button v-b-toggle="'collapse-11'" class="mini-collapsible">
                    e. Task Assignment
                  </b-button>
                  <b-collapse id="collapse-11">
                    <div class="responseText2">
                      <p>Mining pools assign tasks in two ways:</p>
                      <p>
                        1. Pools assign tasks with a range of nonce for a
                        particular miner/group of miners.
                      </p>
                      <p>
                        2. Pools allow members the freedom to pick and choose
                        their own work.
                      </p>
                    </div>
                  </b-collapse>

                  <b-button v-b-toggle="'collapse-12'" class="mini-collapsible">
                    f. Security and Stability
                  </b-button>
                  <b-collapse id="collapse-12">
                    <div class="responseText2">
                      <p>
                        The security and stability of a mining pool’s services
                        are critical in choosing a pool. Miners need to know
                        that the pool will be operational 24/7 and experience
                        little downtime.
                      </p>
                      <p>
                        Before choosing a pool, you should be comfortable with
                        the security and stability that pools advertise on their
                        website.
                      </p>
                    </div>
                  </b-collapse>

                  <b-button v-b-toggle="'collapse-13'" class="mini-collapsible">
                    g. Transparency
                  </b-button>
                  <b-collapse id="collapse-13">
                    <div class="responseText2">
                      <p>
                        Lastly, miners should feel comfortable with the level of
                        communication and data made available to them from the
                        pool. Miners need to know that the hashrate they are
                        being credited for, as well as the total hashrate of the
                        pool are being reported accurately.
                      </p>
                    </div>
                  </b-collapse>
                </div>
              </b-collapse>
            </div>
            <div class="divider">
              <b-button v-b-toggle="'collapse-3'" class="collapsible">
                How do I create a pool account?
              </b-button>
              <b-collapse id="collapse-3">
                <div class="responseText">
                  <p>
                    To create a pool account, you can go onto the website of the
                    pool you wish to mine with and follow their instructions.
                    You will need, at the very least, an address (BTC, ETH, LTC,
                    Dash, etc) for which the pool to send payouts to. Create a
                    username, password, and make sure you have this handy. You
                    can either set the account details now before your miners
                    are deployed, or you can wait until they are operational and
                    hashing to Mine.App to select a pool.
                  </p>
                </div>
              </b-collapse>
            </div>
            <div class="divider">
              <b-button v-b-toggle="'collapse-4'" class="collapsible">
                How do I get paid my mining rewards?
              </b-button>
              <b-collapse id="collapse-4">
                <div class="responseText">
                  <p>
                    Depending on the pool, you will get paid out once your
                    credited rewards reach a certain threshold. Some pools allow
                    you to set this, some require a minimum. Once your credited
                    rewards hit the threshold, the pool will send you your
                    rewards to the address that you provided, less any fees that
                    they take.
                  </p>
                </div>
              </b-collapse>
            </div>
            <div class="divider">
              <b-button v-b-toggle="'collapse-5'" class="collapsible">
                Where can I find more information?
              </b-button>
              <b-collapse id="collapse-5">
                <div class="responseText">
                  <p>
                    For more information on how pools work, please checkout the
                    following resources:
                  </p>
                  <a
                    href="https://www.investopedia.com/tech/how-do-mining-pools-work/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Investopedia - How Do Cryptocurrency Mining Pools Work?
                  </a>
                  <br />
                  <a
                    href="https://www.investopedia.com/tech/how-choose-cryptocurrency-mining-pool/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Investopedia - How to Choose a Cryptocurrency Mining Pool
                  </a>
                  <br />
                  <a
                    href="https://www.nicehash.com/blog/post/how-mining-pools-distribute-rewards-pps-vs-fpps-vs-pplns"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Nicehash - How mining pools distribute rewards?
                  </a>
                  <br />
                  <a
                    href="https://medium.com/luxor/mining-pool-payment-methods-pps-vs-pplns-ac699f44149f"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Luxor on Medium - Different Bitcoin Mining Pool Payment
                    Methods
                  </a>
                </div>
              </b-collapse>
            </div>
          </div>
        </b-col>
      </b-row>
    </b-container>
  </FooterWrapper>
</template>

<style scoped>
.progress {
  height: 0.5rem;
}
.center {
  text-align: center;
}
.right {
  text-align: right;
}
.collapsible {
  background-color: white;
  color: black;
  cursor: pointer;
  padding: 18px;
  width: 100%;
  border: none;
  text-align: left;
  outline: none;
  font-size: 15px;
}
.mini-collapsible {
  background-color: white;
  color: black;
  cursor: pointer;
  padding: 5px;
  width: 100%;
  border: none;
  text-align: left;
  outline: none;
  font-size: 15px;
}
.responseText {
  padding: 18px;
}
.responseText2 {
  padding: 5px;
}
.active,
.collapsible:hover {
  background-color: rgb(151, 151, 151);
}
.divider {
  border-bottom: 1px rgb(230, 230, 230) solid;
}
.content {
  padding: 0 18px;
  display: none;
  overflow: hidden;
  background-color: #f1f1f1;
}
.spacer {
  height: 10px;
}
</style>
